<template>
  <v-container fluid class="product-page pa-6">

    <!-- Page Header -->
    <div class="product-header mb-6">
      <div>
        <div class="breadcrumb-nav mb-2">
          <span
            class="breadcrumb-link"
            @click="$router.push({ name: '/supplier-setup', params: { supplier_id: supplierId } })"
          >
            {{ supplierName }}
          </span>
          <v-icon small class="mx-2" color="#9E9E9E">mdi-chevron-right</v-icon>
          <span class="breadcrumb-current">New Product</span>
        </div>
        <h2 class="product-title">Create New Product</h2>
      </div>
      <v-btn color="primary" rounded elevation="0" class="save-btn" :loading="saving" @click="createProduct">
        <v-icon left small>mdi-plus</v-icon>
        Create Product
      </v-btn>
    </div>

    <v-row>
      <!-- Left column: Form fields -->
      <v-col cols="12" md="8">
        <!-- Product Details -->
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-tag-outline</v-icon>
            Product Details
          </div>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="f.name"
                  label="Product Name *"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-store-outline"
                  required
                  :error="errors.name"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="f.service_type_ids"
                  :items="get_supplier_type"
                  multiple
                  item-text="name"
                  item-value="id"
                  label="Booking Types *"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-view-list-outline"
                  chips small-chips deletable-chips
                  required
                  :error="errors.service_type_ids"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Pricing -->
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-currency-usd</v-icon>
            Pricing
          </div>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="f.customer_price"
                  :label="'Customer Price (' + get_selected_company.currency + ')'"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-account-cash-outline"
                  type="number"
                  min="0"
                  :error="errors.customer_price"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="f.supplier_price"
                  :label="'Supplier Price (' + get_selected_company.currency + ')'"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-cash"
                  type="number"
                  min="0"
                  :error="errors.supplier_price"
                />
              </v-col>
              <!-- Per group: the Supplier Price is the whole group's cost (customer price stays per person) -->
              <v-col cols="12" sm="6" offset-sm="6">
                <v-select
                  v-model="f.supplier_price_type"
                  label="Supplier Price Is"
                  outlined dense hide-details="auto"
                  prepend-inner-icon="mdi-account-group-outline"
                  :items="[{ text: 'Per person', value: 0 }, { text: 'Per group', value: 1 }]"
                  :hint="f.supplier_price_type == 1 ? 'The whole group\'s cost. Profit checks divide it by the group size.' : ''"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Notes -->
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-text-long</v-icon>
            Description &amp; Notes
          </div>
          <v-card-text class="pt-4">
            <label class="field-label">Description</label>
            <wysiwyg v-model="f.description" class="mb-5" />
            <label class="field-label">Admin Notes</label>
            <wysiwyg v-model="f.admin_notes" />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right column: Image upload -->
      <v-col cols="12" md="4">
        <v-card class="section-card mb-4" elevation="0">
          <div class="section-header">
            <v-icon small class="mr-2" color="#525A68">mdi-image-outline</v-icon>
            Product Image
          </div>
          <v-card-text class="pt-4 text-center">
            <div class="image-preview mb-4">
              <img
                v-if="imagePreview"
                :src="imagePreview"
                class="product-preview-img"
              />
              <div v-else class="image-placeholder">
                <v-icon size="56" color="#C8C8C8">mdi-image-plus-outline</v-icon>
                <div class="grey--text text--lighten-1 mt-2 caption">Upload product image</div>
              </div>
            </div>
            
            <v-file-input
              v-model="product_image_file"
              label="Upload Image"
              outlined
              dense
              hide-details="auto"
              accept="image/png, image/jpeg, image/gif"
              prepend-inner-icon="mdi-upload"
              class="mb-4"
              @change="previewImage"
            />

            <div class="d-flex align-center justify-center">
              <v-btn
                color="grey"
                text
                rounded
                small
                :disabled="!product_image_file"
                @click="clearImage"
              >
                <v-icon left small>mdi-close</v-icon>
                Clear
              </v-btn>
            </div>

            <div v-if="product_image_file" class="mt-3">
              <span class="caption grey--text text--darken-1">
                <v-icon x-small class="mr-1">mdi-file-image-outline</v-icon>
                {{ product_image_file.name }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  props: ['supplierId'],
  data: () => ({
    f: {
      name: null,
      supplier_product_type_id: null,
      service_type_ids: [],
      customer_price: null,
      supplier_price: null,
      supplier_price_type: 0,
      description: null,
      admin_notes: null,
    },
    supplierName: '',
    saving: false,
    product_types: [],
    // Image handling
    product_image_file: null,
    imagePreview: null,
    // Validation
    errors: {},
  }),
  async mounted() {
    await this.fetchSupplierName()
    await this.fetchProductTypes()
    await this.$store.dispatch('supplier/fetch_supplier_type')
  },
  computed: {
    ...mapGetters({
      get_selected_company: 'auth/get_selected_company',
      get_supplier_type: 'supplier/get_supplier_type',
    }),
    get_supplier_product_type() {
      return this.product_types
    },
  },
  methods: {
    async fetchSupplierName() {
      const { data } = await this.$axios.get(`admin/suppliers/get_supplier/${this.supplierId}`)
      if (data.response) {
        this.supplierName = data.data.supplier_name
        // Pre-fill with the supplier's own types as a sensible default (still editable) -
        // most products share at least one type with their supplier (e.g. every room at an
        // Accommodation-only supplier), so this saves re-picking the obvious choice each time.
        const supplierTypes = (data.data.service_type_ids && data.data.service_type_ids.length)
          ? data.data.service_type_ids
          : (data.data.service_type_id ? [data.data.service_type_id] : [])
        if (this.f.service_type_ids.length === 0) {
          this.f.service_type_ids = [...supplierTypes]
        }
      }
    },
    async fetchProductTypes() {
      const { data } = await this.$axios.get('api/suppliers/products/get_product_type')
      if (data.response) {
        this.product_types = data.data
      }
    },
    validateForm() {
      this.errors = {}
      
      if (!this.f.name || this.f.name.trim() === '') {
        this.errors.name = true
      }
      
      if (!this.f.service_type_ids || this.f.service_type_ids.length === 0) {
        this.errors.service_type_ids = true
      }

      if (this.f.customer_price && parseFloat(this.f.customer_price) < 0) {
        this.errors.customer_price = true
      }

      if (this.f.supplier_price && parseFloat(this.f.supplier_price) < 0) {
        this.errors.supplier_price = true
      }

      return Object.keys(this.errors).length === 0
    },
    previewImage() {
      if (this.product_image_file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.imagePreview = e.target.result
        }
        reader.readAsDataURL(this.product_image_file)
      } else {
        this.imagePreview = null
      }
    },
    clearImage() {
      this.product_image_file = null
      this.imagePreview = null
    },
    async createProduct() {
      if (!this.validateForm()) {
        this.$toast.error('Please fill in all required fields')
        return
      }

      this.saving = true

      try {
        // Create FormData for file upload
        const formData = new FormData()
        
        // Add product fields
        formData.append('name', this.f.name || '')
        formData.append('supplier_product_type_id', this.f.supplier_product_type_id || '')
        formData.append('service_type_ids', JSON.stringify(this.f.service_type_ids || []))
        formData.append('customer_price', this.f.customer_price || '')
        formData.append('supplier_price', this.f.supplier_price || '')
        formData.append('supplier_price_type', this.f.supplier_price_type ? 1 : 0)
        formData.append('description', this.f.description || '')
        formData.append('admin_notes', this.f.admin_notes || '')
        formData.append('supplier_id', this.supplierId)
        
        // Add image if selected
        if (this.product_image_file) {
          formData.append('product_image', this.product_image_file)
        }

        const { data } = await this.$axios.post('admin/suppliers/create_supplier_product', formData)
        
        if (data.response) {
          this.$toast.success(data.message)
          this.$router.push({ name: '/supplier-setup/product/setup', params: { 
            supplier_id: this.supplierId, 
            product_id: data.data.id 
          } })
        } else {
          this.$toast.error(data.message)
        }
      } catch (error) {
        console.error('Error creating product:', error)
        this.$toast.error('Error creating product. Please try again.')
      }

      this.saving = false
    },
  },
}
</script>
<style scoped lang="scss">
.product-page {
  background-color: #F5F6FA;
  min-height: 100vh;
}

.product-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
}

.breadcrumb-link {
  font-size: 13px;
  font-weight: 600;
  color: #525A68;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.breadcrumb-current {
  font-size: 13px;
  font-weight: 600;
  color: #2C2C2C;
}

.product-title {
  font-size: 20px;
  font-weight: 700;
  color: #2C2C2C;
  letter-spacing: -0.2px;
  line-height: 1.2;
  margin: 0;
}

.save-btn {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0 24px;
  height: 38px;
}

.section-card {
  border-radius: 12px !important;
  border: 1px solid #E8E8E8 !important;
  background-color: #fff;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #7C7C7C;
  background-color: #FAFAFA;
  border-bottom: 1px solid #F0F0F0;
  padding: 10px 16px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #555;
  margin-bottom: 6px;
}

.image-preview {
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #DEDEDE;
}

.product-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
